<template>
  <div :class="segmentedControlClass">
    <button
      v-for="option in options"
      :key="option.value"
      :class="getButtonClass(option.value)"
      :disabled="disabled || isOptionDisabled(option)"
      :title="option.tooltip"
      @click="handleSelect(option.value)"
    >
      <Icon v-if="option.icon" :name="option.icon" :size="iconSize" />
      <span v-if="option.label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from './Icon.vue'

export interface SegmentedControlOption {
  value: string
  label?: string
  icon?: string
  tooltip?: string
  disabled?: boolean | (() => boolean)
}

export interface SegmentedControlProps {
  modelValue: string
  options: SegmentedControlOption[]
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<SegmentedControlProps>(), {
  disabled: false,
  size: 'medium'
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [value: string]
}>()

const segmentedControlClass = computed(() => [
  'segmented-control',
  `segmented-control--${props.size}`,
  {
    'segmented-control--disabled': props.disabled
  }
])

const getButtonClass = (value: string) => [
  'segmented-control__button',
  {
    'segmented-control__button--active': props.modelValue === value,
    'segmented-control__button--cancel': value === 'cancel'
  }
]

const iconSize = computed(() => {
  switch (props.size) {
    case 'small':
      return 12
    case 'large':
      return 16
    default:
      return 14
  }
})

const isOptionDisabled = (option: SegmentedControlOption): boolean => {
  if (option.disabled === undefined) return false
  return typeof option.disabled === 'function' ? option.disabled() : option.disabled
}

const handleSelect = (value: string) => {
  if (props.disabled || value === props.modelValue) return
  emit('update:modelValue', value)
  emit('change', value)
}
</script>

<style scoped lang="scss">
.segmented-control {
  display: inline-flex;
  border-radius: 4px;
  background: var(--vscode-input-background);
  border: 1px solid var(--vscode-input-border);
  overflow: hidden;
  
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.segmented-control__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-right: 1px solid var(--vscode-input-border);
  color: var(--vscode-foreground);
  font-family: var(--vscode-font-family);
  font-size: var(--vscode-font-size);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover:not(&--active) {
    background: var(--vscode-list-hoverBackground);
  }
  
  &:active:not(&--active) {
    background: var(--vscode-list-activeSelectionBackground);
    color: var(--vscode-list-activeSelectionForeground);
  }
  
  &--active {
    background: var(--vscode-button-background);
    color: var(--vscode-button-foreground);
    font-weight: 500;
  }
  
  &:focus,
  &:focus-visible {
    outline: none !important;
  }
  
  &--cancel {
    background: transparent;
    color: var(--vscode-foreground);
    
    &:hover:not(.segmented-control__button--active) {
      background: var(--vscode-statusBarItem-errorBackground, #f14c4c) !important;
      color: var(--vscode-statusBarItem-errorForeground, #ffffff) !important;
    }
    
    &:active:not(.segmented-control__button--active) {
      background: var(--vscode-inputValidation-errorBorder, #be1100) !important;
      color: var(--vscode-statusBarItem-errorForeground, #ffffff) !important;
    }
    
    &.segmented-control__button--active {
      background: var(--vscode-statusBarItem-errorBackground, #f14c4c);
      color: var(--vscode-statusBarItem-errorForeground, #ffffff);
      font-weight: 500;
    }
  }
}

// Size variants
.segmented-control--small {
  .segmented-control__button {
    padding: 4px 8px;
    font-size: 12px;
    gap: 4px;
  }
}

.segmented-control--medium {
  .segmented-control__button {
    padding: 6px 12px;
    font-size: var(--vscode-font-size);
    gap: 6px;
  }
}

.segmented-control--large {
  .segmented-control__button {
    padding: 8px 16px;
    font-size: 14px;
    gap: 8px;
  }
}
</style>
